import { css } from '@emotion/react'
import {
  ColorPrimitives,
  GemColors,
  gemVar,
  Shadow,
  ShadowElevation,
  Spacing,
  wideGamutColor,
} from '@opallabs/gem'

const rootSelector = 'gem-copilot-toggle-button'

export const selectors = {
  root: rootSelector,
  icon: `${rootSelector}-icon`,
  glint: `${rootSelector}-glint`,
}

export const BUTTON_SIZE = Spacing.S * 8
export const BUTTON_INSET = Spacing.XS * 5
// Matches the gem glyph's rendered box, so the glint overlays it exactly.
const ICON_SIZE = 42

// The hover reads as the gem throwing light rather than the button changing
// color: purple, Gem's own, bloomed under a circle that stays black. Two
// layers, because one soft shadow at this size disappears into the page — a
// tight halo carries the edge and a wide one carries the throw.
const GLOW = [
  `0 0 ${Spacing.S * 2}px ${Spacing.XS / 2}px ${wideGamutColor(
    GemColors.P400,
    0.55
  )}`,
  `0 ${Spacing.XS}px ${Spacing.S * 4}px ${Spacing.XS}px ${wideGamutColor(
    GemColors.P400,
    0.35
  )}`,
].join(', ')

export const gemButtonStyle = css({
  ...Shadow[ShadowElevation.E2],
  alignItems: 'center',
  // Near-black in both themes rather than a themed surface: the gem's own
  // gradient is the color here, and it needs a dark field to read against.
  backgroundColor: gemVar(ColorPrimitives.N999),
  blockSize: BUTTON_SIZE,
  borderRadius: '50%',
  border: 'none',
  color: gemVar(ColorPrimitives.N000),
  cursor: 'pointer',
  display: 'flex',
  inlineSize: BUTTON_SIZE,
  insetBlockEnd: BUTTON_INSET,
  insetInlineEnd: BUTTON_INSET,
  justifyContent: 'center',
  pointerEvents: 'auto',
  position: 'fixed',
  transition: 'box-shadow 150ms',
  // Above Radix modal overlays, which sit outside the app's ZIndex tiers
  zIndex: 9999,

  // On hover the gem throws light: the glint catches and a purple bloom lands
  // under a circle that keeps its own colour. Lightening the field was the one
  // move left to a black button, and it read as the black washing out.
  '&:hover': {
    boxShadow: [Shadow[ShadowElevation.E3].boxShadow, GLOW].join(', '),

    [`.${selectors.glint}`]: {
      opacity: 1,
      transform: 'scale(1)',
    },
  },
})

// Sits over the gem rather than inside it, so the star is a separate mark that
// can appear on its own. It shares the glyph's box, so the coordinates it is
// placed at are the glyph's own.
export const glint = css({
  blockSize: ICON_SIZE,
  inlineSize: ICON_SIZE,
  insetBlockStart: (BUTTON_SIZE - ICON_SIZE) / 2,
  insetInlineStart: (BUTTON_SIZE - ICON_SIZE) / 2,
  opacity: 0,
  overflow: 'visible',
  pointerEvents: 'none',
  position: 'absolute',
  // Grows into place from the gem's centre, so it reads as a catch of light
  // rather than a second icon switching on.
  transform: 'scale(0.4)',
  transformOrigin: '50% 50%',
  transition: 'opacity 150ms ease, transform 150ms ease',
})
